import styled from '@emotion/styled';
import type { Theme as MuiTheme } from '@mui/material/styles';
import { Text } from 'components/text/Text';
import { colors } from 'styles/theme/colors';

function isMuiTheme(theme: object): theme is MuiTheme {
  return typeof Reflect.get(theme, 'spacing') === 'function';
}

function mui(theme: object): MuiTheme {
  if (!isMuiTheme(theme)) {
    throw new Error('Expected MUI Theme from ThemeProvider');
  }
  return theme;
}

const hideScrollbars = {
  scrollbarWidth: 'none' as const,
  msOverflowStyle: 'none' as const,
  '&::-webkit-scrollbar': {
    display: 'none',
    width: 0,
    height: 0,
  },
};

/**
 * Total height of file preview panels (e.g. {@link FileUploadSection}) so layout stays stable.
 */
export const DOCUMENT_PREVIEW_HEIGHT_PX = 440;

/**
 * Fills the host panel (e.g. {@link DocumentSheet}) so PDF/image preview can measure width/height and scale one page to fit.
 */
export const FilePreviewRoot = styled('div')({
  position: 'relative',
  flex: '1 1 0%',
  minWidth: 0,
  width: '100%',
  minHeight: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  alignSelf: 'stretch',
  overflow: 'hidden',
  boxSizing: 'border-box',
});

/**
 * PDF preview: width-fitted page; vertical scroll when the page is taller than the panel.
 */
export const PdfCanvasClip = styled('div')({
  flex: '1 1 0%',
  minHeight: 0,
  width: '100%',

  /* 🔥 FIX: allow flex to define height properly */
  height: 'auto',
  alignSelf: 'stretch',

  display: 'flex',
  flexDirection: 'column',
  /** Keep bitmap-sized canvas from being stretched horizontally by flex (was fighting JS width → blank-looking area). */
  alignItems: 'center',

  overflowX: 'hidden',
  overflowY: 'auto',
  ...hideScrollbars,
});

/** Stacked PDF pages + visible scrollbar (certificate modal / scroll-sync pager). */
export const FilePreviewPdfScrollSyncArea = styled('div')({
  flex: 1,
  width: '100%',
  minHeight: 280,
  height: 'min(1200px, calc(100vh - 280px))',
  overflowY: 'auto',
  overflowX: 'hidden',
  overscrollBehavior: 'contain',
  scrollbarWidth: 'thin',
  scrollbarColor: `${colors.grey[500]} ${colors.grey[100]}`,
  '&::-webkit-scrollbar': { width: 12 },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: colors.grey[500],
    borderRadius: '8px',
    border: `3px solid ${colors.grey[100]}`,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: colors.grey[100],
    borderRadius: '8px',
  },
});

export const PreviewCanvas = styled('canvas')({
  display: 'block',
  maxWidth: '100%',
  width: 'auto',
  height: 'auto',
  flexShrink: 0,
  backgroundColor: colors.white,
});

/** Used when {@link FilePreview} gets `pdfRemoteSrc` (http/https) — browser PDF viewer. */
export const PdfEmbeddedIframe = styled('iframe')({
  display: 'block',
  width: '100%',
  height: '100%',
  flex: '1 1 auto',
  minHeight: 0,
  border: 0,
  alignSelf: 'stretch',
  backgroundColor: colors.grey[50],
});

export const ImgPreviewClip = styled('div')({
  flex: '1 1 0%',
  minHeight: 0,
  minWidth: 0,
  width: '100%',
  height: '100%',
  alignSelf: 'stretch',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  ...hideScrollbars,
});

export const PreviewImg = styled('img')({
  maxWidth: '100%',
  maxHeight: '100%',
  width: 'auto',
  height: 'auto',
  objectFit: 'contain',
  display: 'block',
});

export const NonPdfFallback = styled('div')(({ theme }) => ({
  flex: '1 1 0%',
  minHeight: 0,
  width: '100%',
  alignSelf: 'stretch',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: mui(theme).spacing(4),
}));

export const PreviewToolbarFloating = styled('div')(({ theme }) => ({
  position: 'absolute',
  left: '50%',
  bottom: mui(theme).spacing(2),
  transform: 'translateX(-50%)',
  pointerEvents: 'auto',
  display: 'flex',
  alignItems: 'center',
  /* Array theme.spacing requires integer indices ([0,2,4,8,...]). */
  gap: mui(theme).spacing(3),
  padding: mui(theme).spacing(2, 3),
  paddingLeft: mui(theme).spacing(3),
  paddingRight: mui(theme).spacing(1),
  borderRadius: '5px',
  backgroundColor: 'rgba(51, 51, 51, 0.92)',
  boxShadow: '0 4px 14px rgba(25, 25, 27, 0.2)',
  zIndex: 2,
  maxWidth: 'calc(100% - 24px)',
  flexWrap: 'wrap',
  justifyContent: 'center',
  '& .MuiIconButton-root': {
    color: colors.grey[0],
    '&.Mui-disabled': {
      color: colors.grey[600],
    },
  },
}));

export const PageNumberField = styled('input')(({ theme }) => ({
  width: 32,
  height: 24,
  borderRadius: 4,
  border: 'none',
  backgroundColor: colors.grey[700],
  color: colors.grey[0],
  textAlign: 'center',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: mui(theme).typography.fontFamily,
  padding: 0,
  flexShrink: 0,
  MozAppearance: 'textfield',
  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  '&:focus': {
    outline: 'none',
    boxShadow: `0 0 0 1px ${colors.grey[400]}`,
  },
  '&:disabled': {
    opacity: 0.85,
    cursor: 'default',
  },
}));

export const PageNavFraction = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  paddingLeft: mui(theme).spacing(1),
  paddingRight: mui(theme).spacing(1),
}));

export const OfWord = styled('span')(({ theme }) => ({
  ...mui(theme).typography.bodySmall,
  color: colors.grey[0],
  fontWeight: 500,
  fontSize: 13,
  lineHeight: 1,
  userSelect: 'none',
  paddingLeft: 2,
  paddingRight: 2,
}));

export const TotalPagesLabel = styled(Text)({
  color: colors.grey[0],
  fontWeight: 600,
  fontSize: 13,
  minWidth: 16,
  textAlign: 'left',
  fontVariantNumeric: 'tabular-nums',
});
